import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Mail, Instagram, Linkedin, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LuxuryNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [contactHover, setContactHover] = useState(false);
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();

  const navLinks = [
    { name: t('HOME', 'INICIO'), path: '/' },
    { name: 'ABOUT', path: '/about' },
    { name: t('APPROACH', 'ENFOQUE'), path: '/evoke' },
    { name: 'STUDIO', path: '/shop' },
    { name: t('JOURNAL', 'DIARIO'), path: '/blog' },
    { name: t('CONTACT', 'CONTACTO'), path: '/contact' },
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-vainilla">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-24">
            {/* Logo Text - Left Side */}
            <Link to="/" className="flex flex-col">
              <span 
                className="font-serif text-xl md:text-2xl font-bold tracking-tight leading-tight"
                style={{ color: '#B96342' }}
              >
                Margaret Núñez
              </span>
              <span 
                className="text-[10px] md:text-xs tracking-widest uppercase"
                style={{ color: '#78716C' }}
              >
                Founder / Creative Director
              </span>
            </Link>

            {/* Round Buttons - Right Side */}
            <div className="flex items-center gap-2">
              {/* Language Toggle - Always Visible */}
              <button
                onClick={toggleLanguage}
                className="flex items-center justify-center rounded-full border-2 transition-all duration-300 hover:bg-terracota/10"
                style={{
                  width: '44px',
                  height: '44px',
                  borderColor: '#B96342',
                  backgroundColor: '#F5F1E8',
                }}
                aria-label="Toggle language"
                title={language === 'en' ? 'Cambiar a Español' : 'Switch to English'}
              >
                <span 
                  className="text-xs font-bold tracking-wide"
                  style={{ color: '#B96342' }}
                >
                  {language === 'en' ? 'ES' : 'EN'}
                </span>
              </button>

              {/* Contact Button with Hover Expand */}
              <div 
                className="relative cursor-pointer"
                onMouseEnter={() => setContactHover(true)}
                onMouseLeave={() => setContactHover(false)}
                onClick={() => navigate('/contact')}
              >
                <div 
                  className="flex items-center justify-center rounded-full border-2 transition-all duration-300"
                  style={{
                    width: contactHover ? '120px' : '44px',
                    height: '44px',
                    borderColor: '#B96342',
                    backgroundColor: '#F5F1E8',
                  }}
                >
                  <Mail 
                    style={{ 
                      width: '18px', 
                      height: '18px', 
                      color: '#B96342',
                      flexShrink: 0,
                      marginLeft: contactHover ? '-20px' : '0',
                      transition: 'margin 0.3s',
                    }} 
                  />
                  <span 
                    className="font-medium text-sm whitespace-nowrap overflow-hidden transition-all duration-300"
                    style={{ 
                      color: '#B96342',
                      width: contactHover ? '60px' : '0px',
                      marginLeft: contactHover ? '8px' : '0',
                      opacity: contactHover ? 1 : 0,
                    }}
                  >
                    {t('Contact', 'Contacto')}
                  </span>
                </div>
              </div>

              {/* Menu Button - Solid Round */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-center rounded-full transition-all duration-300 hover:opacity-90"
                style={{
                  width: '44px',
                  height: '44px',
                  backgroundColor: '#B96342',
                }}
                aria-label="Toggle menu"
              >
                {isOpen ? (
                  <X style={{ width: '18px', height: '18px', color: '#EDE5D8' }} />
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <div style={{ width: '16px', height: '2px', backgroundColor: '#EDE5D8', borderRadius: '1px' }}></div>
                    <div style={{ width: '10px', height: '2px', backgroundColor: '#EDE5D8', borderRadius: '1px' }}></div>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Creative Full Screen Menu */}
      <div 
        className={`fixed inset-0 z-40 transition-all duration-500 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ backgroundColor: '#EDE5D8' }}
      >
        {/* Subtle Dot Pattern Background */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `radial-gradient(#D4C8B8 1px, transparent 1px)`,
            backgroundSize: '24px 24px',
          }}
        />

        {/* Decorative Gradient Orbs */}
        <div 
          className="absolute top-20 right-1/4 w-64 h-64 rounded-full blur-3xl"
          style={{ backgroundColor: '#B96342', opacity: 0.08 }}
        />
        <div 
          className="absolute bottom-32 left-1/3 w-48 h-48 rounded-full blur-3xl"
          style={{ backgroundColor: '#D4C8B8', opacity: 0.2 }}
        />

        <div className="relative h-full flex">
          {/* Left Side - Navigation Links */}
          <div className="w-1/2 flex flex-col justify-center pl-12 md:pl-24">
            {/* Decorative Star */}
            <div className="mb-8">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10L12 0Z" fill="#B96342" opacity="0.6"/>
              </svg>
            </div>

            <div className="space-y-2">
              {navLinks.map((link, index) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="block text-4xl md:text-6xl font-bold uppercase tracking-tight transition-all duration-300 hover:translate-x-4"
                  style={{
                    color: '#B96342',
                    fontFamily: "'Playfair Display', serif",
                    transform: isOpen ? 'translateX(0)' : 'translateX(-40px)',
                    opacity: isOpen ? 1 : 0,
                    transition: `all 0.5s ease ${index * 0.08}s`,
                  }}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Right Side - Image */}
          <div className="hidden md:flex w-1/2 items-center justify-center pr-12">
            <div 
              className="relative"
              style={{
                transform: isOpen ? 'rotate(-3deg) scale(1)' : 'rotate(0deg) scale(0.9)',
                opacity: isOpen ? 1 : 0,
                transition: 'all 0.6s ease 0.2s',
              }}
            >
              {/* Hand-drawn frame effect */}
              <div 
                className="absolute -inset-4 border-4 border-dashed rounded-lg"
                style={{ borderColor: '#D4C8B8', transform: 'rotate(2deg)' }}
              />
              <img
                src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=500&h=600&fit=crop"
                alt="Experience Design"
                className="w-80 h-96 object-cover shadow-2xl"
                style={{ 
                  clipPath: 'polygon(5% 0%, 100% 0%, 95% 100%, 0% 100%)',
                }}
              />
              {/* Decorative sun rays */}
              <svg 
                className="absolute -top-8 -right-8 w-24 h-24"
                viewBox="0 0 100 100"
                style={{
                  opacity: isOpen ? 1 : 0,
                  transition: 'opacity 0.5s ease 0.5s',
                }}
              >
                <path 
                  d="M50 0 Q55 40 70 30 Q60 45 100 50 Q60 55 70 70 Q55 60 50 100 Q45 60 30 70 Q40 55 0 50 Q40 45 30 30 Q45 40 50 0" 
                  fill="none" 
                  stroke="#B96342" 
                  strokeWidth="2"
                  opacity="0.4"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Bottom Bar - Social Links & Language */}
        <div 
          className="absolute bottom-8 left-0 right-0 px-12 flex items-center justify-center gap-6"
          style={{
            opacity: isOpen ? 1 : 0,
            transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.5s ease 0.4s',
          }}
        >
          <a 
            href="https://instagram.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="p-3 rounded-full transition-colors hover:bg-terracota hover:bg-opacity-10"
          >
            <Instagram style={{ width: '24px', height: '24px', color: '#B96342' }} />
          </a>
          <a 
            href="https://linkedin.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="p-3 rounded-full transition-colors hover:bg-terracota hover:bg-opacity-10"
          >
            <Linkedin style={{ width: '24px', height: '24px', color: '#B96342' }} />
          </a>
          
          {/* Contact Us Button */}
          <Link
            to="/contact"
            onClick={() => setIsOpen(false)}
            className="px-6 py-3 rounded-full border-2 font-medium transition-all hover:bg-terracota hover:text-white"
            style={{ borderColor: '#B96342', color: '#B96342' }}
          >
            {t('Contact Us', 'Contáctanos')}
          </Link>
          
          <a 
            href="mailto:hello@margaretnunez.com"
            className="p-3 rounded-full transition-colors hover:bg-terracota hover:bg-opacity-10"
          >
            <Mail style={{ width: '24px', height: '24px', color: '#B96342' }} />
          </a>
          
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 p-3 rounded-full transition-colors hover:bg-terracota hover:bg-opacity-10"
          >
            <Globe style={{ width: '24px', height: '24px', color: '#B96342' }} />
            <span style={{ color: '#B96342', fontWeight: 500 }}>{language.toUpperCase()}</span>
          </button>
        </div>

        {/* Close Button - Top Right (same position as menu button) */}
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-6 right-6 flex items-center justify-center transition-all duration-300"
          style={{
            width: '44px',
            height: '62px',
            borderRadius: '31px',
            backgroundColor: '#B96342',
          }}
          aria-label="Close menu"
        >
          <X style={{ width: '20px', height: '20px', color: '#EDE5D8' }} />
        </button>
      </div>
    </>
  );
};

export default LuxuryNavbar;
